import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useExecutionsStore } from '../store/executionsStore';
import { fetchRunningNeoTraces } from '../utils/fetchRunningNeoTraces';
import { fetchSerialNeoQueue } from '../utils/fetchSerialNeoQueue';
import { hasActiveNeoAttacksForApplication } from '../utils/neoAttackReconciliation';
import { NEO_ATTACK_RECONCILE_POLL_MS } from '../utils/neoAttackStatus';

type UseNeoAttackReconciliationParams = {
  applicationId?: string;
  enabled?: boolean;
};

export function useNeoAttackReconciliation({
  applicationId,
  enabled = true,
}: UseNeoAttackReconciliationParams) {
  const reconcileNeoAttacksWithBackend = useExecutionsStore(
    (state) => state.reconcileNeoAttacksWithBackend,
  );
  const hasActiveAttacks = useExecutionsStore((state) =>
    applicationId
      ? hasActiveNeoAttacksForApplication({
          cachedEntries: state.neoAttacks,
          applicationId,
        })
      : false,
  );

  const shouldPoll = !!applicationId && enabled && hasActiveAttacks;

  const runningTracesQuery = useQuery({
    queryKey: ['neo-running-traces', applicationId],
    queryFn: () => {
      if (!applicationId) {
        throw new Error('applicationId is required');
      }

      return fetchRunningNeoTraces(applicationId);
    },
    enabled: !!applicationId && enabled,
    refetchInterval: shouldPoll ? NEO_ATTACK_RECONCILE_POLL_MS : false,
    staleTime: 0,
  });
  const serialQueueQuery = useQuery({
    queryKey: ['neo-serial-queue', applicationId],
    queryFn: () => {
      if (!applicationId) {
        throw new Error('applicationId is required');
      }

      return fetchSerialNeoQueue(applicationId);
    },
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? NEO_ATTACK_RECONCILE_POLL_MS : false,
    staleTime: 0,
  });

  useEffect(() => {
    if (!applicationId || !enabled || !runningTracesQuery.data) {
      return;
    }

    reconcileNeoAttacksWithBackend({
      applicationId,
      runningTraces: runningTracesQuery.data,
      serialQueue: serialQueueQuery.data,
    });
  }, [
    applicationId,
    enabled,
    reconcileNeoAttacksWithBackend,
    runningTracesQuery.data,
    serialQueueQuery.data,
  ]);
}
